import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogActions, Button, Chip, Typography, Grid, Link, Box, Divider } from '@mui/material';
import Logo from '../../../../resources/images/logo/bizismart.png';
import { useOrganizationContext } from '../../../../context/organization_context/OrganizationContext';
import { useLoadingContext } from '../../../../context/loading/LoadingContext';
import { ToastType, useToast } from '../../../../context/toast/ToastContext';
import EditEventModal from './EditEventModal';
import { Modules } from '../../../../utils/permissions/Modules';
import { PermissionTypes } from '../../../../utils/permissions/PermissionTypes';
import usePermissions from '../../../../utils/permissions/UsePermissions';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../../resources/strings/StringKeys';
import { useGoogleCalendar } from '../hooks/useGoogleCalendar';
import GoogleIcon from '@mui/icons-material/Google';
import DeleteIcon from '@mui/icons-material/Delete';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import advancedFormat from 'dayjs/plugin/advancedFormat';

dayjs.extend(localizedFormat);
dayjs.extend(advancedFormat);

const EventModal = ({ show, onHide, event, onDelete, onEdit }) => {
    
    const { organizationEmployees, organizationCustomers } = useOrganizationContext();
    const { setLoadingData } = useLoadingContext();
    const { showToast } = useToast();
    const [showEditEventModal, setShowEditEventModal] = useState(false);
    const { hasPermission } = usePermissions();
    const { t } = useTranslation();
    const { deleteGoogleEvent } = useGoogleCalendar();

    // Early return if no event data
    if (!event) {
        return null;
    }

    const isGoogleEvent = event.extendedProps?.source === 'google';
    const isFirebaseEvent = event.extendedProps?.source === 'firebase';
    const isLinkedToGoogle = event.extendedProps?.isGoogleLinked;
    const isHoliday = isGoogleEvent && event.extendedProps?.creator === 'Google Holidays';
    const isRecurringGoogleEvent = isGoogleEvent && event.extendedProps?.recurrence !== null;
    const isCustomEvent = !isGoogleEvent;

    const formatDate = (date, format = 'LLLL') => {
        if (!date) return t(StringKeys.NO_DATE);
        return dayjs(date).format(format);
    };

    const handleDelete = async () => {
        try {
            setLoadingData(true);
            
            if (isFirebaseEvent) {
                // For Firebase events, we need to handle both Firebase and potentially Google deletion
                const result = await onDelete(event.extendedProps.originalId, isLinkedToGoogle ? event.extendedProps.googleEventId : null);
                if (result.success) {
                    showToast(t(StringKeys.SUCCESSFULLY_DELETED_EVENT), ToastType.SUCCESS);
                    onHide();
                } else {
                    showToast(t(StringKeys.FAILED_TO_DELETE_EVENT), ToastType.DANGER);
                }
            } else if (isGoogleEvent) {
                // For Google-only events, we just need to handle Google deletion
                const result = await onDelete(null, event.extendedProps.originalId);
                if (result.success) {
                    showToast(t(StringKeys.SUCCESSFULLY_DELETED_EVENT), ToastType.SUCCESS);
                    onHide();
                } else {
                    showToast(t(StringKeys.FAILED_TO_DELETE_EVENT), ToastType.DANGER);
                }
            }
        } catch (error) {
            console.error('Error deleting event:', error);
            showToast(t(StringKeys.FAILED_TO_DELETE_EVENT), ToastType.DANGER);
        } finally {
            setLoadingData(false);
        }
    };

    const handleEditSuccess = async (updatedEvent) => {
        // Ensure dates are properly handled
        const start = updatedEvent.start instanceof Date ? updatedEvent.start : new Date(updatedEvent.start);
        const end = updatedEvent.end instanceof Date ? updatedEvent.end : new Date(updatedEvent.end);

        // Ensure we maintain the correct event structure
        const updatedEventData = {
            ...event,
            title: updatedEvent.title,
            start,
            end,
            extendedProps: {
                ...event.extendedProps,
                description: updatedEvent.description,
                location: updatedEvent.location,
                job_code: updatedEvent.job_code,
                assigned_employees: updatedEvent.assigned_employees,
                client_id: updatedEvent.client_id,
                is_private: updatedEvent.is_private,
                source: event.extendedProps.source,
                isGoogleLinked: event.extendedProps.isGoogleLinked,
                googleEventId: event.extendedProps.googleEventId,
                originalId: event.extendedProps.originalId
            }
        };

        // Update the event object with the new data
        Object.assign(event, updatedEventData);
        setShowEditEventModal(false);
    };

    const renderGoogleEventDetails = () => (
        <>
            {isHoliday && (
                <Grid item xs={12}>
                    <Typography variant="body1">{t(StringKeys.HOLIDAY)}</Typography>
                </Grid>
            )}
            {!isHoliday && event.extendedProps.creator && (
                <Grid item xs={12}>
                    <Typography variant="body1">
                        <strong>{t(StringKeys.CREATED_BY)}:</strong> {event.extendedProps.creator}
                    </Typography>
                </Grid>
            )}
        </>
    );

    const renderCustomEventDetails = () => {
        const scheduledEmployees = event.extendedProps?.assigned_employees?.map((id) => {
            const employee = organizationEmployees.find((emp) => emp.id === id);
            return employee ? `${employee.first_name} ${employee.last_name}` : t(StringKeys.UNKNOWN);
        }) || [t(StringKeys.NO_EMPLOYEES_ASSIGNED)];

        const scheduledCustomer = event.extendedProps.client_id
            ? organizationCustomers.find((customer) => customer.id === event.extendedProps.client_id)
            : null;

        return (
            <>
                <Grid item xs={12}>
                    <Typography variant="body1">
                        <strong>{t(StringKeys.DESCRIPTION)}:</strong> {event.extendedProps.description || t(StringKeys.NO_DESCRIPTION)}
                    </Typography>
                </Grid>
                {scheduledEmployees.length > 0 && (
                    <Grid item xs={12}>
                        <Typography variant="body1">
                            <strong>{t(StringKeys.SCHEDULED_EMPLOYEES)}:</strong>
                        </Typography>
                        {scheduledEmployees.map((name, index) => (
                            <Chip key={index} label={name} style={{ margin: '5px' }} />
                        ))}
                    </Grid>
                )}
                {scheduledCustomer && (
                    <Grid item xs={12}>
                        <Typography variant="body1">
                            <strong>{t(StringKeys.SCHEDULED_CUSTOMER)}:</strong> {`${scheduledCustomer.first_name} ${scheduledCustomer.last_name}`}
                        </Typography>
                    </Grid>
                )}
                {event.extendedProps.job_code && (
                    <Grid item xs={12}>
                        <Typography variant="body1">
                            <strong>{t(StringKeys.JOB_CODE)}:</strong> {event.extendedProps.job_code}
                        </Typography>
                    </Grid>
                )}
                {event.url && event.url.trim() && (
                    <Grid item xs={12}>
                        <Typography variant="body1">
                            <Link href={event.url} target="_blank" rel="noopener">
                                {t(StringKeys.VIEW_DETAILS)}
                            </Link>
                        </Typography>
                    </Grid>
                )}
                <Grid item xs={12}>
                    <Typography variant="body1">
                        {event.extendedProps.is_private ? t(StringKeys.PRIVATE) : t(StringKeys.PUBLIC)}
                    </Typography>
                </Grid>
            </>
        );
    };

    return (
        <>
            <Dialog open={show} onClose={onHide} aria-labelledby="event-modal-title" maxWidth="md" fullWidth>
                <DialogTitle id="event-modal-title">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <img alt="" src={Logo} width="30" height="30" />
                        {event.title}
                        <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                            {isFirebaseEvent && (
                                <Chip 
                                    label="BiziSmart" 
                                    color="primary" 
                                    size="small" 
                                    icon={<img src={Logo} alt="" style={{ width: 16, height: 16 }} />}
                                    sx={{ backgroundColor: '#023047' }}
                                />
                            )}
                            {(isGoogleEvent || isLinkedToGoogle) && (
                                <Chip 
                                    label="Google Calendar" 
                                    color="primary" 
                                    size="small"
                                    icon={<GoogleIcon />}
                                    sx={{ backgroundColor: '#023047' }}
                                />
                            )}
                            {isHoliday && (
                                <Chip 
                                    label={t(StringKeys.HOLIDAY)} 
                                    color="secondary" 
                                    size="small" 
                                />
                            )}
                        </Box>
                    </div>
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <Typography variant="body1">
                                <strong>{t(StringKeys.START)}:</strong> {formatDate(event.start, 'dddd, MMMM D, YYYY h:mm A')}
                            </Typography>
                        </Grid>
                        {event.end && (
                            <Grid item xs={6}>
                                <Typography variant="body1">
                                    <strong>{t(StringKeys.END)}:</strong> {formatDate(event.end, 'dddd, MMMM D, YYYY h:mm A')}
                                </Typography>
                            </Grid>
                        )}
                        {event.extendedProps.location && (
                            <Grid item xs={12}>
                                <Typography variant="body1">
                                    <strong>{t(StringKeys.LOCATION)}:</strong> {event.extendedProps.location}
                                </Typography>
                            </Grid>
                        )}
                        {isGoogleEvent ? renderGoogleEventDetails() : renderCustomEventDetails()}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    {hasPermission(Modules.CALENDAR, PermissionTypes.WRITE) && (
                        <>
                            <Button
                                variant="contained"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={handleDelete}
                            >
                                {t(StringKeys.DELETE)}
                            </Button>
                            {!isHoliday && (
                                <Button 
                                    color="primary" 
                                    variant="contained"
                                    onClick={() => setShowEditEventModal(true)}
                                >
                                    {t(StringKeys.EDIT)}
                                </Button>
                            )}
                        </>
                    )}
                    <Button onClick={onHide}>{t(StringKeys.CLOSE)}</Button>
                </DialogActions>
            </Dialog>
            {showEditEventModal && (
                <EditEventModal
                    show={showEditEventModal}
                    onHide={() => setShowEditEventModal(false)}
                    event={event}
                    onEdit={async (eventData) => {
                        try {
                            const result = await onEdit(eventData, event);
                            if (result.success) {
                                await handleEditSuccess(eventData);
                            }
                            return result;
                        } catch (error) {
                            console.error('Error in edit handler:', error);
                            return { success: false, error };
                        }
                    }}
                />
            )}
        </>
    );
};

export default EventModal;
